import React, { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import { asUnified, ut } from '@devnw/theme'
import type { AdminUserFactorItem } from '../../../gen/client/react/client'
import { useAdminGenClient } from '../api/genClient'
import {
  CrudTable,
  CrudDangerButton,
  PageStack,
  PageTitle,
  SectionBlock,
} from '../components/crud'
import type { CrudColumn } from '../components/crud'
import { PamLoading, PamError, PamEmpty } from '../components/PamStateComponents'
import { useActiveTenant } from '../layout/TenantSwitcher'

function errMessage(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

const KIND_LABELS: Record<string, string> = {
  totp: 'Authenticator (TOTP)',
  webauthn: 'Passkey / security key',
  mtls: 'mTLS certificate',
}

const BackLink = styled(Link)`
  align-self: flex-start;
  font-size: ${({ theme }) => asUnified(theme).font?.sizes?.sm ?? '0.875rem'};
  color: ${({ theme }) => asUnified(theme).colors?.primary ?? '#1a56db'};
`

const PageStackWithGap = styled(PageStack)`
  gap: ${({ theme }) => ut(asUnified(theme)).space('sm')};
`

/**
 * Admin per-user factor inventory — Task 11 (fix-wave 2026-07-24).
 *
 * Distinct from WebAuthnCredentials.tsx (self-service, the CALLER's own
 * passkeys only): this is the genuine ADMIN surface, backed by
 * AdminListUserFactors/AdminRevokeUserFactor (admin_user_factors.go:152,173),
 * which return non-secret metadata (kind/id/name/createdAt) across a user's
 * TOTP/WebAuthn/mTLS factors — never a TOTP secret, WebAuthn public key,
 * sign counter, or recovery material. Linked from Users.tsx per-row.
 *
 * Gating: no dedicated Permission constant in pam_permissions.go gates these
 * two handlers for an ordinary human admin caller — adminUserFactorsGate
 * calls only enforceUserAdmin (enforceAdminMultiTenant: base admin session,
 * any admin role, no fine-grained permission check; verified against
 * admin_user_factors.go and admin_authz_helpers.go's
 * customMachineRoutePermission, which maps PermTenantUsersList only to the
 * plain list-users route for app-bound service callers, not to the factors
 * sub-route). Inventing a requiredPermission here would misrepresent
 * enforcement that doesn't exist server-side, so this route is left
 * ungated beyond AdminLayout's outer `requireAdmin` — the SAME fail-closed
 * fallback already used by its sibling admin pages that have no matching
 * ROUTE_PERMISSIONS entry (Users/Tenants/Connectors/Clients/MtlsCAs; see
 * routePermissions.ts's own "why not every /admin/* route is listed" note).
 */
export const UserFactors = (): JSX.Element => {
  const { userId } = useParams<{ userId: string }>()
  const { tenant } = useActiveTenant()
  const client = useAdminGenClient()

  const [factors, setFactors] = useState<AdminUserFactorItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revokingKey, setRevokingKey] = useState<string | null>(null)

  const load = useCallback(async (): Promise<void> => {
    if (!userId) return
    setLoading(true)
    setError(null)
    try {
      const resp = await client.adminListUserFactors({ tenantId: tenant, userId })
      setFactors(resp.factors ?? [])
    } catch (e: unknown) {
      setError(errMessage(e, 'Failed to load user factors'))
    } finally {
      setLoading(false)
    }
  }, [client, tenant, userId])

  useEffect(() => {
    load()
  }, [load])

  const handleRevoke = useCallback(async (factor: AdminUserFactorItem): Promise<void> => {
    if (!userId) return
    const key = `${factor.kind}:${factor.id}`
    setRevokingKey(key)
    setError(null)
    try {
      await client.adminRevokeUserFactor({
        tenantId: tenant,
        userId,
        factorKind: factor.kind,
        factorId: factor.id,
      })
      setFactors((prev) => prev.filter((f) => `${f.kind}:${f.id}` !== key))
    } catch (e: unknown) {
      setError(errMessage(e, 'Failed to revoke user factor'))
    } finally {
      setRevokingKey(null)
    }
  }, [client, tenant, userId])

  const columns: CrudColumn<AdminUserFactorItem>[] = [
    { key: 'kind', header: 'Type', render: (row) => KIND_LABELS[row.kind] ?? row.kind },
    { key: 'name', header: 'Name', render: (row) => row.name ?? '—' },
    { key: 'createdAt', header: 'Enrolled', render: (row) => formatDate(row.createdAt) },
  ]

  const rowActions = (row: AdminUserFactorItem): React.ReactNode => {
    const key = `${row.kind}:${row.id}`
    return (
      <CrudDangerButton
        type="button"
        disabled={revokingKey === key}
        onClick={() => handleRevoke(row)}
      >
        Revoke
      </CrudDangerButton>
    )
  }

  return (
    <PageStackWithGap>
      <BackLink to="/admin/users">Back to Users</BackLink>
      <PageTitle>User Factors{userId ? ` — ${userId}` : ''}</PageTitle>
      <SectionBlock>
        {loading && <PamLoading label="Loading user factors…" />}
        {!loading && error && <PamError message={error} onRetry={load} />}
        {!loading && !error && factors.length === 0 && (
          <PamEmpty message="No factors enrolled for this user." />
        )}
        {/* Rendered whenever there is data to show, even alongside a
            just-failed mutation (e.g. a revoke that failed) — a mutation
            error must never make an already-loaded, still-accurate list
            disappear (mirrors Users.tsx's rs.error/actionError + CrudTable
            co-rendering convention). */}
        {!loading && factors.length > 0 && (
          <CrudTable
            columns={columns}
            items={factors}
            rowActions={rowActions}
            getRowKey={(row) => `${row.kind}:${row.id}`}
            caption="User factors"
          />
        )}
      </SectionBlock>
    </PageStackWithGap>
  )
}
